import { useMemo, useState } from "react";
import { X, Crown, Zap, Lock, Check, Sparkles, MapPin, Bell, Eye, Store } from "lucide-react";
import { getProductLink } from "../services/BillingService";
import { useUserContext } from "../context/UserContext";
import { track } from "../utils/analytics";
import { hasAnyActiveRetailerPlan } from "../utils/subscriptionGuards";
import { getFreeIntentCopy, getFreeUserIntent } from "../utils/freeExperience";

const BASIC_PRICE_ID = "price_1SpvXMG2cPZes0aXe9CPsPMK";
const PLUS_PRICE_ID = "price_1TfTddG2cPZes0aXmDKAbVOu";
const PRO_PRICE_ID = "price_1SpvZXG2cPZes0aXeCwoqTIa";

interface Plan {
  id: string;
  name: string;
  price: number;
  discountedPrice: number;
  priceId: string;
  description: string;
  features: string[];
  highlight?: boolean;
  icon: React.ReactNode;
  badge?: string;
}

const plans: Plan[] = [
  {
    id: "basic",
    name: "Basic",
    price: 34.99,
    discountedPrice: 17.5,
    priceId: BASIC_PRICE_ID,
    description: "Perfect for getting started",
    icon: <Zap className="w-5 h-5" />,
    features: [
      "1 Walmart + 1 HD Location",
      "Discord Community Access",
      "Push Notifications & Alerts",
      "Favorites & Tracking",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    price: 59.99,
    discountedPrice: 30,
    priceId: PLUS_PRICE_ID,
    description: "More stores, more deals",
    icon: <Sparkles className="w-5 h-5" />,
    features: [
      "3 Walmart + 3 HD Locations",
      "Discord Community Access",
      "Push Notifications & Alerts",
      "Favorites & Tracking",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    discountedPrice: 49.5,
    priceId: PRO_PRICE_ID,
    description: "For serious deal hunters",
    icon: <Crown className="w-5 h-5" />,
    highlight: true,
    badge: "Best Value",
    features: [
      "5 Walmart + 5 HD Locations",
      "5 Target + 5 Dollar General Locations",
      "Advanced local insights + higher limits",
      "Priority Support",
    ],
  },
];

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  title?: string;
  description?: string;
  highlights?: string[];
  ctaLabel?: string;
  targetPlan?: "pro";
}

export default function UpgradeModal({
  isOpen,
  onClose,
  feature,
  title,
  description,
  ctaLabel,
  targetPlan,
}: UpgradeModalProps) {
  const { user } = useUserContext();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasActivePlan = hasAnyActiveRetailerPlan(user);
  const freeUserIntent = getFreeUserIntent();
  const intentCopy = getFreeIntentCopy(freeUserIntent);
  const visiblePlans = useMemo(
    () => (targetPlan === "pro" ? plans.filter((plan) => plan.id === "pro") : plans),
    [targetPlan]
  );

  const isProTarget = targetPlan === "pro";
  const isMultiPlan = visiblePlans.length > 1;

  if (!isOpen) return null;

  const handlePlanSelect = async (plan: Plan) => {
    if (hasActivePlan) {
      if (isProTarget) {
        window.location.href = "/settings?tab=billing&upgradeSource=upgrade_modal";
        onClose();
        return;
      }
      setError(
        "You already have an active plan. Please manage billing to upgrade or cancel your existing plan."
      );
      return;
    }
    setLoading(plan.id);
    setError(null);

    try {
      track("upgrade-modal-plan-selected", { planId: plan.id, planName: plan.name, priceId: plan.priceId });
      const response = await getProductLink([plan.priceId]);
      if (response?.link) {
        window.location.href = response.link;
      } else {
        throw new Error("No checkout link received");
      }
    } catch (err) {
      console.error("Failed to get checkout link:", err);
      setError("Failed to start checkout. Please try again.");
      track("upgrade-modal-checkout-error", { planId: plan.id, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(null);
    }
  };

  const headerTitle = title || (isProTarget ? "Upgrade to Pro" : "Unlock the full experience");
  const headerSubtitle =
    description ||
    (feature
      ? `${feature} is part of the full DealHawk experience.`
      : isProTarget
        ? "More store locations and higher limits across every retailer."
        : intentCopy.upgradeHook);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4 sheet-backdrop-in"
      onClick={onClose}
    >
      <div
        className="flex max-h-[94vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl dark:bg-dark-900 sm:rounded-3xl sheet-panel-in-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 overflow-hidden bg-gradient-to-br from-purple-600 via-purple-600 to-indigo-600 px-5 pb-5 pt-5">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-white/10 blur-2xl" />
          <button
            onClick={onClose}
            className="absolute right-3 top-3 rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="relative flex items-center gap-3 pr-8">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
              {isProTarget ? <Crown className="h-5 w-5 text-white" /> : <Sparkles className="h-5 w-5 text-white" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight text-white">{headerTitle}</h2>
              <p className="mt-0.5 text-sm leading-snug text-white/85">{headerSubtitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* 50% OFF Badge */}
          <div className="mb-5 flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-900/40 dark:bg-amber-900/20">
            <Sparkles className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            <span className="text-sm font-bold text-amber-700 dark:text-amber-300">50% OFF your first month</span>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-center text-sm text-red-600 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Value comparison — only for free users picking a plan */}
          {!isProTarget && (
            <div className="mb-5 overflow-hidden rounded-2xl border border-gray-200 dark:border-dark-700">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 bg-gray-50 px-4 py-2 dark:bg-dark-800">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-400">What you get</span>
                <span className="w-16 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-500">Free</span>
                <span className="w-16 text-center text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-300">Paid</span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-dark-700/60">
                {[
                  { icon: <Eye className="h-4 w-4" />, label: "Online deals", free: "All", paid: "All" },
                  { icon: <Store className="h-4 w-4" />, label: "Local stores", free: "—", paid: "Up to 5" },
                  { icon: <Bell className="h-4 w-4" />, label: "Price alerts", free: "—", paid: "Up to 10" },
                  { icon: <MapPin className="h-4 w-4" />, label: "Nearby search", free: "1/day", paid: "∞" },
                ].map((row) => (
                  <div key={row.label} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-3 px-4 py-2.5">
                    <div className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-dark-200">
                      <span className="text-gray-400 dark:text-dark-500">{row.icon}</span>
                      <span className="font-medium">{row.label}</span>
                    </div>
                    <span className="w-16 text-center text-xs text-gray-400 dark:text-dark-500">{row.free}</span>
                    <span className="w-16 text-center text-xs font-bold text-purple-600 dark:text-purple-300">{row.paid}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plan Cards */}
          <div className="space-y-3">
            {visiblePlans.map((plan) => {
              const featured = plan.highlight || !isMultiPlan;
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border p-4 transition-colors ${
                    featured
                      ? "border-purple-300 bg-purple-50/50 dark:border-purple-700/60 dark:bg-purple-900/10"
                      : "border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800"
                  }`}
                >
                  {plan.badge && isMultiPlan && (
                    <div className="absolute -top-2.5 left-4 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                      {plan.badge}
                    </div>
                  )}

                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                      <p className="text-xs text-gray-500 dark:text-dark-400">{plan.description}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <div className="flex items-baseline justify-end gap-1.5">
                        <span className="text-xl font-black text-gray-900 dark:text-white">${plan.discountedPrice.toFixed(2)}</span>
                        <span className="text-xs text-gray-400 line-through">${plan.price}</span>
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-dark-500">/mo first month</p>
                    </div>
                  </div>

                  <ul className="mb-4 space-y-1.5">
                    {plan.features.map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-dark-300">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-600 dark:text-purple-400" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => !loading && handlePlanSelect(plan)}
                    disabled={!!loading}
                    className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition-all active:scale-[0.99] ${
                      featured
                        ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:opacity-95"
                        : "bg-gray-900 text-white hover:bg-gray-800 dark:bg-dark-700 dark:hover:bg-dark-600"
                    } ${loading ? "opacity-60" : ""}`}
                  >
                    {loading === plan.id ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Processing...
                      </>
                    ) : (
                      ctaLabel ? `${ctaLabel} with ${plan.name}` : `Get ${plan.name}`
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Maybe Later */}
          <button
            onClick={onClose}
            className="mt-3 w-full rounded-xl py-2.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-dark-800"
          >
            Maybe later
          </button>

          {/* Footer Note */}
          <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 dark:text-gray-500">
            <Lock className="h-3 w-3" />
            Cancel anytime · Secure checkout via Stripe
          </p>
        </div>
      </div>
    </div>
  );
}

// Compact upgrade banner for inline use
export function UpgradeBanner({
  onUpgrade,
  message,
  subMessage,
  ctaLabel,
  hiddenCount,
}: {
  onUpgrade: () => void;
  message?: string;
  subMessage?: string;
  ctaLabel?: string;
  hiddenCount?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-gradient-to-r from-purple-600/10 to-indigo-600/10 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-800/50 rounded-lg sm:rounded-xl overflow-hidden">
      <div className="p-2 sm:p-4 flex items-center justify-between gap-2 sm:gap-4">
        <div
          className="flex items-center gap-1.5 sm:gap-3 min-w-0 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="p-1 sm:p-2 bg-purple-100 dark:bg-purple-900/50 rounded-md sm:rounded-lg flex-shrink-0">
            <Lock className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm leading-snug font-medium text-gray-900 dark:text-white">
              {message || "Upgrade to see more products"}
            </p>
            <p className="hidden sm:block text-xs text-gray-500 dark:text-dark-400">
              {subMessage || "You're viewing limited results on the free tier"}
            </p>
          </div>
        </div>
        <button onClick={onUpgrade} className="px-2.5 sm:px-4 py-1 sm:py-2 rounded-lg bg-purple-600 text-white text-xs sm:text-sm font-medium hover:bg-purple-700 transition-colors flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap">
          <Zap className="hidden sm:block w-3.5 h-3.5" />
          {ctaLabel || "Upgrade"}
        </button>
      </div>
      {expanded && (
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0 border-t border-purple-200/50 dark:border-purple-800/30">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {hiddenCount != null && hiddenCount > 0 && (
              <div className="flex flex-col items-center rounded-lg bg-white/80 dark:bg-dark-800/80 px-2 py-2 text-center">
                <span className="text-lg font-black text-purple-700 dark:text-purple-300">{hiddenCount.toLocaleString()}+</span>
                <span className="text-[10px] text-gray-500 dark:text-dark-400 leading-tight">more deals hidden</span>
              </div>
            )}
            <div className="flex flex-col items-center rounded-lg bg-white/80 dark:bg-dark-800/80 px-2 py-2 text-center">
              <MapPin className="w-4 h-4 text-purple-500 mb-0.5" />
              <span className="text-[10px] text-gray-500 dark:text-dark-400 leading-tight">Local store deals</span>
            </div>
            <div className="flex flex-col items-center rounded-lg bg-white/80 dark:bg-dark-800/80 px-2 py-2 text-center">
              <Bell className="w-4 h-4 text-purple-500 mb-0.5" />
              <span className="text-[10px] text-gray-500 dark:text-dark-400 leading-tight">Price & stock alerts</span>
            </div>
            <div className="flex flex-col items-center rounded-lg bg-white/80 dark:bg-dark-800/80 px-2 py-2 text-center">
              <Store className="w-4 h-4 text-purple-500 mb-0.5" />
              <span className="text-[10px] text-gray-500 dark:text-dark-400 leading-tight">Inventory lookup</span>
            </div>
          </div>
          <button
            onClick={onUpgrade}
            className="mt-2 w-full py-2.5 rounded-lg bg-purple-600 text-white text-xs sm:text-sm font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5"
          >
            See Plans & Upgrade
          </button>
        </div>
      )}
    </div>
  );
}
